// SignaturePad.jsx
import React, { useRef, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SignaturePad = ({ onSave, onCancel, disabled = false }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [ctx, setCtx] = useState(null);

  // Initialisation du canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Ajuster la taille du canvas pour qu'il soit net
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (!container) return;

      const width = container.clientWidth;
      const height = 200; // Hauteur fixe

      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.lineWidth = 2;
      context.strokeStyle = '#000000'; // Noir pour la signature
      
      // Fond blanc
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Ligne de guidage en gris clair
      context.beginPath();
      context.strokeStyle = '#e5e7eb'; // Gris clair pour la ligne de guidage
      context.lineWidth = 1;
      context.setLineDash([5, 5]);
      context.moveTo(20, canvas.height / 2);
      context.lineTo(canvas.width - 20, canvas.height / 2);
      context.stroke();
      context.setLineDash([]);
      
      // Texte indicatif en gris
      context.font = '12px system-ui, -apple-system, sans-serif';
      context.fillStyle = '#9ca3af'; // Gris pour le texte indicatif
      context.fillText('Signez ici', 20, canvas.height - 20);
      
      setCtx(context);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // Démarrer le dessin
  const startDrawing = (e) => {
    if (disabled) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.strokeStyle = '#000000'; // S'assurer que la couleur est noire
      ctx.lineWidth = 2;
      setIsDrawing(true);
      setHasSignature(true);
    }
  };

  const startDrawingTouch = (e) => {
    if (disabled) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const x = (touch.clientX - rect.left) * (canvas.width / rect.width);
    const y = (touch.clientY - rect.top) * (canvas.height / rect.height);

    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.strokeStyle = '#000000'; // S'assurer que la couleur est noire
      ctx.lineWidth = 2;
      setIsDrawing(true);
      setHasSignature(true);
    }
  };

  // Dessiner
  const draw = (e) => {
    if (!isDrawing || disabled) return;

    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const drawTouch = (e) => {
    if (!isDrawing || disabled) return;

    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const x = (touch.clientX - rect.left) * (canvas.width / rect.width);
    const y = (touch.clientY - rect.top) * (canvas.height / rect.height);

    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  // Arrêter le dessin
  const stopDrawing = () => {
    if (ctx) {
      ctx.closePath();
    }
    setIsDrawing(false);
  };

  // Effacer la signature
  const clearSignature = () => {
    if (!ctx || disabled) return;
    
    const canvas = canvasRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Remettre la ligne de guidage en gris
    ctx.beginPath();
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.moveTo(20, canvas.height / 2);
    ctx.lineTo(canvas.width - 20, canvas.height / 2);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Texte indicatif en gris
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = '#9ca3af';
    ctx.fillText('Signez ici', 20, canvas.height - 20);
    
    setHasSignature(false);
  };

  // Sauvegarder la signature
  const saveSignature = () => {
    if (!hasSignature || disabled) return;
    
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    onSave(signatureData);
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-[200px] border-2 border-border rounded-lg cursor-crosshair touch-none bg-white"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawingTouch}
          onTouchMove={drawTouch}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
        
        {disabled && (
          <div className="absolute inset-0 bg-black/5 cursor-not-allowed rounded-lg" />
        )}
      </div>

      <div className="flex justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={clearSignature}
          disabled={disabled || !hasSignature}
          iconName="Eraser"
        >
          Effacer
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={disabled}
            iconName="X"
          >
            Annuler
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={saveSignature}
            disabled={disabled || !hasSignature}
            iconName="Check"
          >
            Confirmer la signature
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Signez dans la zone ci-dessus avec votre souris ou votre doigt (sur tablette)
      </p>
    </div>
  );
};

export default SignaturePad;